import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';

interface ReviewState {
  id: string;
  overall_rating: number;
  quality_rating: number | null;
  communication_rating: number | null;
  timeliness_rating: number | null;
  price_fairness_rating: number | null;
  review_text: string | null;
  reviewer_display_name: string;
  job_title: string | null;
  response_deadline: string | null;
  created_at: string;
}

interface LocationState {
  review?: ReviewState;
}

function daysRemaining(deadline: string): number {
  return Math.max(0, Math.ceil((new Date(deadline).getTime() - Date.now()) / 86400000));
}

function Stars({ rating }: { rating: number }) {
  return (
    <span style={{ color: '#f59e0b', fontSize: 16 }}>
      {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
    </span>
  );
}

const MIN_LENGTH = 10;
const MAX_LENGTH = 500;

export function RespondToReview() {
  const { reviewId } = useParams<{ reviewId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;
  const review = state.review;

  const [responseText, setResponseText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/reviews/${reviewId}/respond`, { response_text: responseText }),
    onSuccess: () => setSubmitted(true),
    onError: (err) => {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong. Please try again.',
      );
    },
  });

  if (!review) {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <p style={{ color: '#6b7280', fontSize: 15, textAlign: 'center' }}>
          Review data not found.
        </p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{ marginTop: 16, padding: '10px 20px', background: '#1D4ED8', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }}
        >
          Go back
        </button>
      </div>
    );
  }

  const days = review.response_deadline ? daysRemaining(review.response_deadline) : null;
  const isValid = responseText.length >= MIN_LENGTH && responseText.length <= MAX_LENGTH;

  if (submitted) {
    return (
      <div style={{ minHeight: '100dvh', background: '#f9fafb', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ width: 64, height: 64, background: '#f0fdf4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 16 }}>
          ✓
        </div>
        <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#111827' }}>Response posted</h2>
        <p style={{ margin: '0 0 24px', fontSize: 14, color: '#6b7280', textAlign: 'center', maxWidth: 280 }}>
          Your response is now visible alongside the review.
        </p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{ padding: '12px 28px', background: '#1D4ED8', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{ background: '#1D4ED8', padding: '48px 20px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            width: 36, height: 36, borderRadius: 10, cursor: 'pointer', fontSize: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          &#8592;
        </button>
        <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: 0 }}>
          Respond to Review
        </h1>
      </div>

      <div style={{ padding: '20px 16px 120px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Deadline badge */}
        {days !== null && (
          <div style={{
            background: days <= 2 ? '#fef2f2' : '#fffbeb',
            border: `1px solid ${days <= 2 ? '#fecaca' : '#fde68a'}`,
            borderRadius: 12, padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ fontSize: 16 }}>{days <= 2 ? '⚠️' : '⏱'}</span>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: days <= 2 ? '#dc2626' : '#92400e' }}>
              {days > 0
                ? `${days} day${days === 1 ? '' : 's'} remaining to respond`
                : 'Response window has closed'}
            </p>
          </div>
        )}

        {/* Original review (read-only) */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <p style={{ margin: '0 0 2px', fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Original Review
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '6px 0 10px' }}>
            <div>
              <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#111827' }}>
                {review.reviewer_display_name}
              </p>
              {review.job_title && (
                <p style={{ margin: '1px 0 0', fontSize: 12, color: '#9ca3af' }}>{review.job_title}</p>
              )}
            </div>
            <Stars rating={review.overall_rating} />
          </div>
          {review.review_text ? (
            <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
              {review.review_text}
            </p>
          ) : (
            <p style={{ margin: 0, fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>
              No written comment.
            </p>
          )}
        </div>

        {/* Tips card */}
        <div style={{
          background: '#eff6ff', borderRadius: 14, padding: '14px 16px',
          border: '1px solid #bfdbfe',
        }}>
          <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: '#1D4ED8' }}>
            Tips for a great response
          </p>
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13, color: '#1e40af', lineHeight: 1.7 }}>
            <li>Thank the reviewer for their feedback</li>
            <li>Address specific points professionally</li>
            <li>Keep it concise — under 3 sentences is ideal</li>
            <li>Avoid defensive language</li>
          </ul>
        </div>

        {/* Response textarea */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: 15, color: '#111827', marginBottom: 8 }}>
            Your Response
          </label>
          <textarea
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            maxLength={MAX_LENGTH}
            placeholder="Thank you for taking the time to share your experience..."
            style={{
              width: '100%', height: 120, padding: '10px 12px',
              border: `1.5px solid ${responseText.length > 0 && responseText.length < MIN_LENGTH ? '#fca5a5' : '#e5e7eb'}`,
              borderRadius: 10, fontSize: 14, resize: 'vertical', outline: 'none',
              fontFamily: 'inherit', boxSizing: 'border-box', color: '#111827',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>Min {MIN_LENGTH} characters</span>
            <span style={{
              fontSize: 12,
              color: responseText.length > MAX_LENGTH * 0.9 ? '#f59e0b' : '#9ca3af',
            }}>
              {responseText.length}/{MAX_LENGTH}
            </span>
          </div>
        </div>

        {/* Immutability warning */}
        <div style={{
          background: '#fefce8', borderRadius: 12, padding: '10px 14px',
          border: '1px solid #fef08a', display: 'flex', gap: 8,
        }}>
          <span style={{ fontSize: 14, flexShrink: 0 }}>⚠️</span>
          <p style={{ margin: 0, fontSize: 13, color: '#713f12' }}>
            Responses cannot be edited after submitting.
          </p>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: 14 }}>
            <p style={{ margin: 0, color: '#dc2626', fontSize: 14 }}>{error}</p>
          </div>
        )}
      </div>

      {/* Fixed submit bar */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        padding: '12px 16px 28px',
        background: '#fff', borderTop: '1px solid #f3f4f6',
      }}>
        <button
          type="button"
          onClick={() => { setError(null); mutation.mutate(); }}
          disabled={!isValid || mutation.isPending}
          style={{
            width: '100%', padding: 14,
            background: isValid && !mutation.isPending ? '#1D4ED8' : '#e5e7eb',
            color: isValid && !mutation.isPending ? '#fff' : '#9ca3af',
            border: 'none', borderRadius: 12,
            fontSize: 15, fontWeight: 600,
            cursor: isValid && !mutation.isPending ? 'pointer' : 'not-allowed',
          }}
        >
          {mutation.isPending ? 'Posting...' : 'Post Response'}
        </button>
      </div>
    </div>
  );
}
